import { useCallback, useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'
import { IDentalChart } from '@dataTypes/chartingFeature.types'
import { StreamingVoicePhase } from '@hooks/useStreamingVoiceSession'
import {
  useVoiceTranscriptFeed,
  VoiceTranscriptEntry,
} from '@hooks/useVoiceTranscriptFeed'
import { getAccessToken } from '@services/auth/accessTokenStore'
import { getEnv } from '@utils/env'
import {
  createDentalKeywordState,
  DentalKeywordState,
  DentalVoiceCursor,
} from '@utils/dental-voice-keyword.utils'
import { pcmToBase64 } from '@utils/perio-audio.utils'

type StopOptions = {
  preserveMessage?: boolean
  message?: string
}

type DentalVoiceReadyPayload = {
  sessionId: string
  model: string
  cursor: DentalVoiceCursor
  message: string
}

type DentalVoiceTranscriptPayload = {
  sessionId: string
  itemId: string
  text: string
  formatted?: string
  interpreted?: string
}

type DentalVoiceChartPatchPayload = {
  sessionId: string
  chart: IDentalChart
  cursor: DentalVoiceCursor
  message: string
  toothChanged?: number
}

type DentalVoiceMessagePayload = {
  sessionId: string
  message: string
  cursor?: DentalVoiceCursor
}

type DentalVoiceErrorPayload = {
  sessionId?: string
  message: string
  code?: string
}

const chartingBase = (): string => {
  const base = getEnv('REACT_APP_BUSINESS_LAYER_CHARTING').replace(/\/$/, '')
  if (!base) throw new Error('The charting service URL is not configured.')
  return base
}

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `dental_voice_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export function useDentalStreamingVoice({
  enabled,
  contextKey,
  chart,
  onChart,
  onToothChange,
}: {
  enabled: boolean
  contextKey: string
  chart: IDentalChart
  onChart: (next: IDentalChart) => void
  onToothChange?: (tooth: number) => void
}): {
  phase: StreamingVoicePhase
  message: string
  toggle: () => void
  stop: () => void
  keyword: DentalKeywordState
  backlog: number
  recognized: string
  liveText: string
  history: VoiceTranscriptEntry[]
  queued: string[]
  selectCursor: (patch: Partial<DentalVoiceCursor>) => void
} {
  const [phase, setPhase] = useState<StreamingVoicePhase>('idle')
  const [message, setMessage] = useState('')
  const [keyword, setKeyword] = useState(createDentalKeywordState)
  const [backlog, setBacklog] = useState(0)
  const feed = useVoiceTranscriptFeed()
  const feedRef = useRef(feed)
  feedRef.current = feed
  const latest = useRef({ chart, enabled, contextKey, onChart, onToothChange })
  latest.current = { chart, enabled, contextKey, onChart, onToothChange }
  const keywordRef = useRef(keyword)
  const generation = useRef(0)
  const running = useRef(false)
  const resources = useRef<{
    stream?: MediaStream
    audio?: AudioContext
    node?: AudioWorkletNode
    socket?: Socket
    sessionId?: string
  }>({})

  const updateKeyword = useCallback((next: DentalKeywordState): void => {
    keywordRef.current = next
    setKeyword(next)
  }, [])

  const stop = useCallback(
    (options?: StopOptions): void => {
      const wasRunning = running.current
      generation.current++
      running.current = false
      const old = resources.current
      resources.current = {}
      if (old.socket && old.sessionId) {
        old.socket.emit('dental_voice:stop', { sessionId: old.sessionId })
      }
      old.socket?.removeAllListeners()
      old.socket?.disconnect()
      old.node?.disconnect()
      old.stream?.getTracks().forEach((track) => track.stop())
      void old.audio?.close()
      feedRef.current.discard()
      setBacklog(0)
      if (wasRunning) {
        updateKeyword({ ...keywordRef.current, paused: false })
        setPhase('idle')
        if (!options?.preserveMessage) {
          setMessage(
            options?.message ??
              'Stopped. Any unfinished phrase was not applied.',
          )
        }
      }
    },
    [updateKeyword],
  )

  useEffect(() => {
    return (): void => stop()
  }, [contextKey, enabled, stop])

  useEffect(() => {
    feedRef.current.reset()
    setMessage('')
    updateKeyword(createDentalKeywordState())
  }, [contextKey, updateKeyword])

  const start = async (): Promise<void> => {
    if (!latest.current.enabled || running.current) return
    running.current = true
    feedRef.current.reset()
    setBacklog(0)
    updateKeyword(createDentalKeywordState(keywordRef.current.cursor))
    const gen = ++generation.current
    const key = latest.current.contextKey
    const sessionId = createSessionId()
    let proxyReady = false
    let resultItemId: string | undefined
    const valid = (): boolean =>
      gen === generation.current &&
      latest.current.enabled &&
      key === latest.current.contextKey
    const fail = (error: unknown): void => {
      if (!valid()) return
      const reason =
        error instanceof Error
          ? error.message
          : 'Unable to connect streaming transcription.'
      stop()
      setPhase('error')
      setMessage(reason)
    }

    setPhase('connecting')
    setMessage('Connecting streaming transcription...')
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          'Microphone capture is unavailable. Use a supported browser over HTTPS (or localhost for development).',
        )
      }
      const token = getAccessToken()
      const dbName = localStorage.getItem('db_name')
      if (!token) throw new Error('Sign in again before using Dental voice.')
      const socket = io(chartingBase(), {
        path: '/api/v1/charting/socket.io/',
        transports: ['websocket'],
        reconnection: false,
        autoConnect: false,
        auth: {
          token,
          dbName: dbName ?? undefined,
        },
      })
      resources.current.socket = socket
      resources.current.sessionId = sessionId

      socket.on('connect_error', (error) => fail(error))
      socket.on('disconnect', (reason) => {
        if (running.current && valid()) {
          fail(new Error(`Voice connection closed: ${reason}`))
        }
      })
      socket.on('dental_voice:ready', (payload: DentalVoiceReadyPayload) => {
        if (!valid() || payload.sessionId !== sessionId) return
        proxyReady = true
        updateKeyword(createDentalKeywordState(payload.cursor))
        setPhase('listening')
        setMessage(payload.message)
      })
      socket.on(
        'dental_voice:partial',
        (payload: DentalVoiceTranscriptPayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          feedRef.current.partial(payload.itemId, payload.text)
        },
      )
      socket.on(
        'dental_voice:final',
        (payload: DentalVoiceTranscriptPayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          feedRef.current.finalize(
            payload.itemId,
            payload.interpreted ?? payload.text,
            { heard: payload.text },
          )
          setBacklog((value) => Math.max(0, value - 1))
        },
      )
      socket.on(
        'dental_voice:message',
        (payload: DentalVoiceMessagePayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          setMessage(payload.message)
          resultItemId = feedRef.current.annotate({
            note: payload.message,
            status: 'failed',
          })
          if (payload.cursor) {
            updateKeyword({ ...keywordRef.current, cursor: payload.cursor })
          }
        },
      )
      socket.on(
        'dental_voice:chart_patch',
        (payload: DentalVoiceChartPatchPayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          latest.current.onChart(payload.chart)
          updateKeyword({ ...keywordRef.current, cursor: payload.cursor })
          if (payload.toothChanged !== undefined) {
            latest.current.onToothChange?.(payload.toothChanged)
          }
          setMessage(payload.message)
          feedRef.current.annotate({
            id: resultItemId,
            note: payload.message,
            status: 'applied',
          })
          resultItemId = undefined
        },
      )
      socket.on('dental_voice:error', (payload: DentalVoiceErrorPayload) => {
        if (!valid()) return
        if (payload.sessionId && payload.sessionId !== sessionId) return
        fail(new Error(payload.message))
      })
      socket.on(
        'dental_voice:stopped',
        (payload: { sessionId: string; reason: string }) => {
          if (!valid() || payload.sessionId !== sessionId) return
          stop({
            preserveMessage: payload.reason === 'voice-command',
            message: 'Microphone stopped. Click the microphone to start again.',
          })
        },
      )

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      if (!valid()) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      resources.current.stream = stream
      const audio = new AudioContext({ sampleRate: 24000 })
      resources.current.audio = audio
      await audio.audioWorklet.addModule('/perio-stream-capture.js')
      if (!valid()) return
      if (audio.sampleRate !== 24000) {
        throw new Error(
          'This browser cannot capture at the required sample rate.',
        )
      }
      socket.on('connect', () => {
        if (!valid()) return
        socket.emit('dental_voice:start', {
          sessionId,
          patient_id: latest.current.chart.patient_id,
          clinic_id: latest.current.chart.clinic_id,
          chart: latest.current.chart,
          cursor: keywordRef.current.cursor,
        })
      })
      const node = new AudioWorkletNode(audio, 'perio-stream-capture')
      resources.current.node = node
      node.onprocessorerror = (): void =>
        fail(
          new Error(
            'Audio capture failed. Restart and repeat unfinished speech.',
          ),
        )
      const mute = audio.createGain()
      mute.gain.value = 0
      node.port.onmessage = (
        event: MessageEvent<{
          type: string
          audio?: Float32Array
          error?: string
        }>,
      ): void => {
        if (!valid()) return
        if (!proxyReady) return
        try {
          if (event.data.type === 'error') throw new Error(event.data.error)
          if (event.data.type === 'audio' && event.data.audio) {
            socket.emit('dental_voice:audio.append', {
              sessionId,
              audio: pcmToBase64(event.data.audio),
            })
          } else if (event.data.type === 'commit') {
            setBacklog((value) => value + 1)
            socket.emit('dental_voice:audio.commit', { sessionId })
          }
        } catch (error) {
          fail(error)
        }
      }
      audio.createMediaStreamSource(stream).connect(node)
      node.connect(mute).connect(audio.destination)
      await audio.resume()
      socket.connect()
      stream.getTracks().forEach((track) => {
        track.onended = (): void =>
          fail(new Error('Microphone disconnected. Restart to continue.'))
      })
    } catch (error) {
      fail(error)
    }
  }

  const toggle = (): void => {
    if (running.current) stop()
    else void start()
  }

  const selectCursor = useCallback(
    (patch: Partial<DentalVoiceCursor>): void => {
      const nextCursor = { ...keywordRef.current.cursor, ...patch }
      if (keywordRef.current.cursor.tooth === nextCursor.tooth) return
      updateKeyword({
        ...keywordRef.current,
        cursor: nextCursor,
      })
      setBacklog(0)
      feedRef.current.discard()
      const sessionId = resources.current.sessionId
      if (running.current && resources.current.socket && sessionId) {
        resources.current.socket.emit('dental_voice:cursor.update', {
          sessionId,
          tooth: nextCursor.tooth,
        })
        setMessage(
          `Position changed to tooth ${nextCursor.tooth}. Unfinished speech was discarded. Still listening - pause briefly and repeat any unfinished phrase.`,
        )
      }
    },
    [updateKeyword],
  )

  return {
    phase,
    message,
    toggle,
    stop,
    keyword,
    backlog,
    recognized: feed.recognized,
    liveText: feed.liveText,
    history: feed.history,
    queued: feed.queued,
    selectCursor,
  }
}
